/**
 * Runtime performance monitoring (field data)
 * ---------------------------------------------------------------------------
 * The build-time budget in `scripts/check-perf-budget.ts` catches oversized
 * assets before deploy. This module catches the regressions that only show up
 * on real devices AFTER deploy: slow LCP, layout shift, and slow image loads.
 *
 * What it measures on the page it is mounted on:
 *   - LCP  (Largest Contentful Paint)  via PerformanceObserver
 *   - CLS  (Cumulative Layout Shift, windowed per session)
 *   - INP-ish first-input delay (when the browser exposes `first-input`)
 *   - per-image load duration from the Resource Timing entries
 *
 * Reporting is privacy friendly: only numbers, an element hint and the
 * surface id go out — no URLs beyond same-origin asset paths, no PII, and
 * everything runs through the consent-gated `track()` pipeline.
 *
 * Metrics are flushed once, when the page is hidden or unloaded, so a single
 * visit produces at most one `web_vitals` event plus one `perf_alert` per
 * breached threshold.
 */
import { track } from "@/lib/analytics";
import { captureWarning } from "@/lib/monitoring";

export type VitalRating = "good" | "needs-improvement" | "poor";

/** Alert thresholds — [good ceiling, poor floor]. Web-Vitals defaults. */
export const PERF_THRESHOLDS = {
  /** Largest Contentful Paint, ms. */
  lcp: [2500, 4000],
  /** Cumulative Layout Shift, unitless. */
  cls: [0.1, 0.25],
  /** First Input Delay, ms. */
  fid: [100, 300],
  /** Time to First Byte, ms. */
  ttfb: [800, 1800],
  /** Single image resource load duration, ms. */
  image: [1200, 2500],
} as const satisfies Record<string, readonly [number, number]>;

export type PerfMetricName = keyof typeof PERF_THRESHOLDS;

export function rate(metric: PerfMetricName, value: number): VitalRating {
  const [good, poor] = PERF_THRESHOLDS[metric];
  if (value <= good) return "good";
  if (value <= poor) return "needs-improvement";
  return "poor";
}

/** Rounds to a sane precision: sub-ms noise is not signal. */
function round(metric: PerfMetricName, value: number): number {
  return metric === "cls" ? Math.round(value * 1000) / 1000 : Math.round(value);
}

export type PerfSnapshot = {
  surface: string;
  lcp?: number;
  cls?: number;
  fid?: number;
  ttfb?: number;
  /** Slowest same-origin image on the page, ms. */
  slowestImage?: number;
  /** File name of the slowest image (path only, no query string). */
  slowestImageName?: string;
  /** Number of images that breached the image threshold. */
  slowImages?: number;
  /** Viewport width bucket, for desktop vs. mobile comparison. */
  viewport?: "mobile" | "tablet" | "desktop";
};

function viewportBucket(): PerfSnapshot["viewport"] {
  const w = window.innerWidth;
  if (w < 640) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

function assetName(url: string): string {
  try {
    return new URL(url, window.location.origin).pathname.split("/").pop()?.slice(0, 80) ?? "asset";
  } catch {
    return "asset";
  }
}

function isImage(entry: PerformanceResourceTiming): boolean {
  return (
    entry.initiatorType === "img" ||
    entry.initiatorType === "image" ||
    /\.(avif|webp|png|jpe?g|gif|svg)(\?|$)/i.test(entry.name)
  );
}

/** Reads image load timings that already happened (no observer needed). */
export function collectImageTimings(): Pick<
  PerfSnapshot,
  "slowestImage" | "slowestImageName" | "slowImages"
> {
  try {
    const entries = performance.getEntriesByType("resource") as PerformanceResourceTiming[];
    const images = entries.filter(isImage);
    if (images.length === 0) return {};
    let slowest = images[0]!;
    let slow = 0;
    for (const e of images) {
      if (e.duration > slowest.duration) slowest = e;
      if (e.duration > PERF_THRESHOLDS.image[0]) slow += 1;
    }
    return {
      slowestImage: Math.round(slowest.duration),
      slowestImageName: assetName(slowest.name),
      slowImages: slow,
    };
  } catch {
    return {};
  }
}

type ObserverStop = () => void;

function observe(
  type: string,
  cb: (entries: PerformanceEntry[]) => void,
  opts: PerformanceObserverInit = {},
): ObserverStop {
  try {
    const po = new PerformanceObserver((list) => cb(list.getEntries()));
    po.observe({ type, buffered: true, ...opts } as PerformanceObserverInit);
    return () => {
      try {
        cb(po.takeRecords());
        po.disconnect();
      } catch {
        /* noop */
      }
    };
  } catch {
    return () => {};
  }
}

const started = new Set<string>();

/**
 * Starts field monitoring for a surface (e.g. "vision").
 * Safe to call from a `useEffect`; returns a cleanup that flushes early.
 * No-ops on the server, for bots, and on repeated calls for the same surface.
 */
export function startPerfMonitoring(surface: string): () => void {
  if (typeof window === "undefined" || typeof PerformanceObserver === "undefined") return () => {};
  if (started.has(surface)) return () => {};
  started.add(surface);

  const snapshot: PerfSnapshot = { surface };
  let flushed = false;

  try {
    const nav = performance.getEntriesByType("navigation")[0] as
      | PerformanceNavigationTiming
      | undefined;
    if (nav && nav.responseStart > 0) snapshot.ttfb = Math.round(nav.responseStart);
  } catch {
    /* noop */
  }

  const stopLcp = observe("largest-contentful-paint", (entries) => {
    const last = entries[entries.length - 1] as (PerformanceEntry & { startTime: number }) | undefined;
    if (last) snapshot.lcp = Math.round(last.startTime);
  });

  let cls = 0;
  const stopCls = observe("layout-shift", (entries) => {
    for (const e of entries as Array<PerformanceEntry & { value: number; hadRecentInput: boolean }>) {
      if (!e.hadRecentInput) cls += e.value;
    }
    snapshot.cls = cls;
  });

  const stopFid = observe("first-input", (entries) => {
    const first = entries[0] as (PerformanceEntry & { processingStart: number }) | undefined;
    if (first) snapshot.fid = Math.round(first.processingStart - first.startTime);
  });

  const flush = () => {
    if (flushed) return;
    flushed = true;
    stopLcp();
    stopCls();
    stopFid();
    Object.assign(snapshot, collectImageTimings());
    snapshot.viewport = viewportBucket();
    reportSnapshot(snapshot);
  };

  const onHidden = () => {
    if (document.visibilityState === "hidden") flush();
  };
  document.addEventListener("visibilitychange", onHidden);
  window.addEventListener("pagehide", flush);
  // Safety net for very long sessions: report after 20s of dwell time.
  const timer = window.setTimeout(flush, 20_000);

  return () => {
    window.clearTimeout(timer);
    document.removeEventListener("visibilitychange", onHidden);
    window.removeEventListener("pagehide", flush);
    flush();
  };
}

/** Sends one summary event plus one alert per breached threshold. */
export function reportSnapshot(snapshot: PerfSnapshot): void {
  const ratings: Record<string, VitalRating> = {};
  const checks: Array<[PerfMetricName, number | undefined]> = [
    ["lcp", snapshot.lcp],
    ["cls", snapshot.cls],
    ["fid", snapshot.fid],
    ["ttfb", snapshot.ttfb],
    ["image", snapshot.slowestImage],
  ];

  const payload: Record<string, unknown> = {
    surface: snapshot.surface,
    viewport: snapshot.viewport,
    slow_images: snapshot.slowImages ?? 0,
    slowest_image: snapshot.slowestImageName,
  };

  for (const [metric, value] of checks) {
    if (typeof value !== "number" || !isFinite(value)) continue;
    const r = rate(metric, value);
    ratings[metric] = r;
    payload[metric] = round(metric, value);
    payload[`${metric}_rating`] = r;
    if (r === "poor") {
      track("perf_alert", {
        surface: snapshot.surface,
        metric,
        value: round(metric, value),
        threshold: PERF_THRESHOLDS[metric][1],
        viewport: snapshot.viewport,
        ...(metric === "image" ? { asset: snapshot.slowestImageName } : {}),
      });
      captureWarning(`perf threshold breached: ${metric}=${round(metric, value)}`, {
        surface: `perf_${snapshot.surface}`,
        metric,
        value: round(metric, value),
        threshold: PERF_THRESHOLDS[metric][1],
      });
    }
  }

  if (Object.keys(ratings).length === 0) return;
  track("web_vitals", payload);
}
